import React,{useState,useEffect} from 'react';
import {useParams,useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useForm} from 'react-hook-form';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {useSupabase} from '../contexts/SupabaseContext';
import {useAuth} from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const {FiSave,FiArrowLeft,FiInfo,FiGlobe}=FiIcons;

const EditCampaign=()=> {
  const {id}=useParams();
  const [loading,setLoading]=useState(false);
  const [fetchLoading,setFetchLoading]=useState(true);
  const navigate=useNavigate();
  const supabase=useSupabase();
  const {user}=useAuth();
  const {register,handleSubmit,setValue,formState: {errors}}=useForm();

  useEffect(()=> {
    fetchCampaign();
  },[id]);

  const fetchCampaign=async ()=> {
    try {
      const {data,error}=await supabase
        .from('campaigns')
        .select('*')
        .eq('id',id)
        .eq('owner_id',user.id)
        .single();
      
      if (error) throw error;

      // Populate form with existing data
      Object.keys(data).forEach(key=> {
        if (key==='names' || key==='cities' || key==='allowed_domains') {
          setValue(key,data[key] ? data[key].join(',') : '');
        } else {
          setValue(key,data[key]);
        }
      });
    } catch (error) {
      toast.error('Failed to fetch campaign details');
      console.error('Error:',error);
      navigate('/dashboard');
    } finally {
      setFetchLoading(false);
    }
  };

  const onSubmit=async (data)=> {
    setLoading(true);
    try {
      const updateData={
        ...data,
        names: data.names ? data.names.split(',').map(s=> s.trim()).filter(Boolean) : null,
        cities: data.cities ? data.cities.split(',').map(s=> s.trim()).filter(Boolean) : null,
        allowed_domains: data.allowed_domains ? data.allowed_domains.split(',').map(s=> s.trim()).filter(Boolean) : null,
        price: data.price ? parseFloat(data.price) : null,
        updated_at: new Date().toISOString()
      };

      const {error}=await supabase
        .from('campaigns')
        .update(updateData)
        .eq('id',id)
        .eq('owner_id',user.id);
      
      if (error) throw error;
      
      toast.success('Campaign updated successfully!');
      navigate(`/campaigns/${id}`);
    } catch (error) {
      toast.error('Failed to update campaign');
      console.error('Error:',error);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{duration: 0.6}}
        >
          <div className="flex items-center mb-8">
            <button 
              onClick={()=> navigate(`/campaigns/${id}`)} 
              className="mr-4 p-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Campaign</h1>
              <p className="text-gray-600 mt-1">Update your social proof notifications</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campaign Name
                  </label>
                  <input 
                    {...register('name',{required: 'Campaign name is required'})}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="My Awesome Campaign"
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Name
                  </label>
                  <input 
                    {...register('product_name',{required: 'Product name is required'})}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Premium Package"
                  />
                  {errors.product_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.product_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price (Optional)
                  </label>
                  <input 
                    {...register('price')}
                    type="number"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="99.99"
                  />
                </div>
              </div>

              {/* Message Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Template
                </label>
                <textarea 
                  {...register('message_template',{required: 'Message template is required'})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="{{name}} from {{city}} just purchased {{product_name}}"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Use {`{{name}}`}, {`{{city}}`}, and {`{{product_name}}`} as placeholders
                </p>
                {errors.message_template && (
                  <p className="mt-1 text-sm text-red-600">{errors.message_template.message}</p>
                )}
              </div>

              {/* Timing Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Interval (ms)
                  </label>
                  <input 
                    {...register('interval_min_ms',{
                      required: 'Min interval is required',
                      min: {value: 5000,message: 'Minimum 5000ms'}
                    })}
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.interval_min_ms && (
                    <p className="mt-1 text-sm text-red-600">{errors.interval_min_ms.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Interval (ms)
                  </label>
                  <input 
                    {...register('interval_max_ms',{
                      required: 'Max interval is required',
                      min: {value: 10000,message: 'Minimum 10000ms'}
                    })}
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.interval_max_ms && (
                    <p className="mt-1 text-sm text-red-600">{errors.interval_max_ms.message}</p>
                  )}
                </div>
              </div>

              {/* Custom Data */}
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Names (Optional)
                  </label>
                  <input 
                    {...register('names')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John,Sarah,Mike,Lisa"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Comma-separated list of names. Leave empty for default names.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Cities (Optional)
                  </label>
                  <input 
                    {...register('cities')}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Jakarta,Surabaya,Bandung,Medan"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Comma-separated list of cities. Leave empty for default cities.
                  </p>
                </div>
              </div>

              {/* Advanced URL Restrictions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SafeIcon icon={FiGlobe} className="h-4 w-4 mr-1 inline" />
                  Allowed URLs (Optional)
                </label>
                <input 
                  {...register('allowed_domains')}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="https://pay.candradimuka.id/*, https://pay.candradimuka.id/#/about"
                />
                
                <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-start">
                    <SafeIcon icon={FiInfo} className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="text-left">
                      <p className="text-sm text-blue-800 font-medium mb-2">URL Restriction Options:</p>
                      <div className="space-y-1 text-xs text-blue-700">
                        <p><strong>Wildcard (for all pages):</strong> <code className="bg-blue-100 px-1 rounded">https://pay.candradimuka.id/*</code></p>
                        <p><strong>Specific URLs (comma-separated):</strong></p>
                        <code className="bg-blue-100 px-1 rounded block mt-1">
                          https://pay.candradimuka.id/#/,<br/>
                          https://pay.candradimuka.id/#/about,<br/>
                          https://pay.candradimuka.id/#/products
                        </code>
                        <p className="mt-2"><strong>No restrictions:</strong> Leave empty to allow all domains</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center">
                <input 
                  {...register('is_active')}
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  Campaign is active
                </label>
              </div>

              <div className="flex justify-end space-x-4">
                <button 
                  type="button"
                  onClick={()=> navigate(`/campaigns/${id}`)}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <SafeIcon icon={FiSave} className="h-4 w-4 mr-2" />
                  )}
                  Update Campaign
                </button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EditCampaign;